import { Button } from '@design/components/button/Button';
import { Icon } from '@design/components/icon/Icon';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { WatchlistError } from '@terminal/stores/use-watchlist/watchlist-types';
import { AlertMessage } from '@terminal/views/onboarding/alert-message/AlertMessage';
import { useAlertMessage } from '@terminal/views/onboarding/hooks/useAlertMessage';
import clsx from 'clsx';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatWatchlistManager } from '../chat-watchlist-manager-widget/ChatWatchlistManagerWidget';
import { TableWatchlistManagerWidget } from '../table-watchlist-manager-widget/TableWatchlistManagerWidget';
import { AssetFilter } from './components/asset-filter/AssetFilter';
import { ConfirmationModal } from './components/confirmation-modal/ConfirmationModal';
import { UpgradePrompt } from './components/upgrade-prompt/UpgradePrompt';
import { WatchlistManagerHeader } from './components/watchlist-manager-header/WatchlistManagerHeader';
import { useEntityChangeHandlers } from './useEntityChangeHandlers';
import { useEntityResolution } from './useEntityResolution';
import { useNavigationBlocker } from './useNavigationBlocker';
import { useWatchlistFilterSync } from './useWatchlistFilterSync';
import { useWatchlistForm } from './useWatchlistForm';
import { useWatchlistManagerMode } from './useWatchlistManagerMode';
import { useWatchlistManagerSave } from './useWatchlistManagerSave';
import { buildRowSelectionFromTags } from './utils';
import styles from './WatchlistManagerWidget.module.scss';

export interface WatchlistManagerWidgetProps {
  onListCreated: (id: string) => void;
  onCancel?: () => void;
  className?: string;
}

export const WatchlistManagerWidget = ({
  className,
  onListCreated,
  onCancel,
}: WatchlistManagerWidgetProps) => {
  const { t } = useTranslation(['watchlistManager', 'common', 'widget']);

  const unMappedRowRef = useRef<HTMLTableRowElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const didEditPrefillRef = useRef(false);
  const [prefillTags, setPrefillTags] = useState<string[]>([]);

  const [chatInputState, setChatInputState] = useState({
    value: '',
    isAssetMessage: true,
  });

  const { filterGroups, resetFilterGroups } = useWatchlist(state => ({
    filterGroups: state.filterGroups,
    resetFilterGroups: state.resetFilterGroups,
  }));

  const {
    watchlistId,
    isAddMode,
    isEditMode,
    onConfirmCallback,
    activeListInAddMode,
    activeListInEditMode,
    activeTags,
  } = useWatchlistManagerMode();

  const {
    listName,
    isRenameActive,
    entitiesList,
    unMappedEntitiesList,
    resolvedEntityData,
    allDuplicateTickers,
    rowSelection,
    manualEntityTags,
    countEntitiesAdded,
    savingInProgress,
    watchlistError,
    watchlistErrorText,
    hasExceededUsageLimit,
    createListLoading,
    hasUnsavedChanges,
    hasEntities,
    selectedEntitiesCount,
    setIsRenameActive,
    onUpdateName,
    resetName,
    setSavingInProgress,
    setResolvedEntityData,
    setEntitiesList,
    setUnMappedEntitiesList,
    setAllDuplicateTickers,
    clearStore,
    updateEntitiesList,
    entityAddedClear,
    clearEntityAddedTimeout,
    validateForm,
    handleValidationError,
    onToggleAllRows,
    onRowSelectionChange,
    createNewList,
    setRowSelection,
    setWatchlistError,
  } = useWatchlistForm();

  const { hasNextPage: hasMoreEntitiesToLoad, isLoadingEntities } =
    useWatchlistFilterSync({
      filterGroups,
      containerRef,
      entitiesListLength: entitiesList.length,
      prefillTags,
      manualEntityTags,
      setEntitiesList,
      setRowSelection,
    });

  const { isPending, onChatSubmit, onSelectItem, getIsInWatchlist } =
    useEntityResolution({
      entitiesList,
      unMappedEntitiesList,
      setResolvedEntityData,
      setUnMappedEntitiesList,
      setAllDuplicateTickers,
      setWatchlistError,
      updateEntitiesList,
      entityAddedClear,
    });

  const { alertMessage, setMessage, setIsError } = useAlertMessage({
    resolvedEntityData,
    allDuplicateTickers,
  });

  useLayoutEffect(
    function initializeEditModePrefill() {
      if (!isEditMode || !activeListInEditMode || didEditPrefillRef.current) {
        return;
      }

      const nextTags = activeListInEditMode.entities.map(e => e.tag);

      if (nextTags.length) {
        setRowSelection(buildRowSelectionFromTags(nextTags));
        entityAddedClear(nextTags.length);
      }

      setPrefillTags(nextTags);
      didEditPrefillRef.current = true;
    },
    [isEditMode, activeListInEditMode, setRowSelection, entityAddedClear]
  );

  const { onConfirm, onSave } = useWatchlistManagerSave({
    watchlistId,
    isAddMode,
    activeListInAddMode,
    activeListInEditMode,
    activeTags,
    onConfirmCallback,
    listName,
    setSavingInProgress,
    validateForm,
    handleValidationError,
    createNewList,
    resetName,
    updateEntitiesList,
    onListCreated,
  });

  useEffect(() => setMessage(''), []);

  useEffect(() => {
    return () => {
      clearStore();
      resetFilterGroups();
      clearEntityAddedTimeout();
    };
  }, []);

  const entityChangeHandlers = useEntityChangeHandlers(setRowSelection);

  const { isBlocked, confirmAndProceed, proceedWithoutSaving, resetBlocker } =
    useNavigationBlocker({
      savingInProgress,
      hasUnsavedChanges,
      hasExceededUsageLimit,
      onConfirm,
    });

  const onChatInputValueChange = (value: string, isAssetMessage = false) => {
    setChatInputState({ value, isAssetMessage });
  };

  const clearAlertMessage = () => {
    setMessage('');
    setIsError(false);
  };

  const showActionButtons =
    (hasEntities || (!!isEditMode && selectedEntitiesCount > 0)) &&
    (chatInputState.isAssetMessage || !chatInputState.value);

  const isEntitiesOverflowing =
    !!containerRef.current &&
    containerRef.current?.scrollHeight > containerRef.current?.clientHeight;

  return (
    <div
      className={clsx(styles.container, className)}
      data-testid="watchlist-manager-widget"
    >
      <AlertMessage
        tableRef={unMappedRowRef}
        alertMessage={alertMessage}
        clearMessage={clearAlertMessage}
        isEntitiesOverflowing={isEntitiesOverflowing}
        insideModal
      />
      <WatchlistManagerHeader
        listName={listName}
        onUpdateName={onUpdateName}
        isRenameActive={isRenameActive}
        setIsRenameActive={setIsRenameActive}
        hasEntities={hasEntities}
        hasInputError={!!watchlistErrorText}
        activeListName={activeListInEditMode?.name ?? activeListInAddMode?.name}
      />
      <div>
        <AssetFilter isPending={isPending} />
      </div>
      {entitiesList.length === 0 && unMappedEntitiesList.length === 0 && (
        <div className={styles.separator}>
          <div className={styles.separatorLine} />
          <span className={styles.separatorText}>{t('common:or')}</span>
          <div className={styles.separatorLine} />
        </div>
      )}
      <div className={styles.contentContainer}>
        <TableWatchlistManagerWidget
          data={entitiesList}
          countEntitiesAdded={countEntitiesAdded}
          toggleAllRowsSelection={onToggleAllRows}
          rowSelection={rowSelection}
          updateRowSelection={onRowSelectionChange}
          containerRef={containerRef}
          unMappedRowRef={unMappedRowRef}
          isEmptyListError={watchlistError === WatchlistError.EmptyList}
          handlers={entityChangeHandlers}
          isPending={isPending}
          hasNextPage={hasMoreEntitiesToLoad}
          isLoadingEntities={isLoadingEntities}
        />
        {!hasExceededUsageLimit && (
          <>
            {(hasEntities || activeListInEditMode) && (
              <p className={styles.label}>
                {t('watchlistManager:wantAddMore')}
              </p>
            )}
            <ChatWatchlistManager
              value={chatInputState.value}
              isPending={isPending}
              getIsInWatchlist={getIsInWatchlist}
              onSelectItem={onSelectItem}
              onChatSubmit={onChatSubmit}
              onChange={onChatInputValueChange}
              activeListEntityTags={activeTags}
            />
          </>
        )}
      </div>
      <UpgradePrompt hide={!hasExceededUsageLimit} />
      {showActionButtons && (
        <div className={styles.footerContainer}>
          {watchlistErrorText && (
            <>
              <Icon
                className={styles.styledIcon}
                size={20}
                iconName="ExclamationCircle"
                fillColor="var(--icon-critical)"
              />
              <p className={styles.errorMessage} data-testid="error-message">
                {watchlistErrorText}
              </p>
            </>
          )}
          <div className={styles.flexContainer}>
            {onCancel && (
              <Button
                label={t('common:cancel')}
                variant="tertiary"
                size="large"
                onClick={onCancel}
              />
            )}
            <Button
              label={
                activeListInEditMode
                  ? t('widget:watchlist.saveChanges')
                  : activeListInAddMode
                    ? t(
                        'watchlistManager:confirmationModal.addAssetsToWatchlist',
                        {
                          count: selectedEntitiesCount,
                        }
                      )
                    : t('watchlistManager:confirmationModal.save')
              }
              variant="primary"
              size="large"
              onClick={onSave}
              isLoading={createListLoading}
              disabled={createListLoading || hasExceededUsageLimit}
              data-testid="watchlist-manager-confirm-button"
            />
          </div>
        </div>
      )}
      {isBlocked && (
        <ConfirmationModal
          onCancel={proceedWithoutSaving}
          onClose={resetBlocker}
          onConfirm={confirmAndProceed}
          hasActiveListInAddMode={!!activeListInAddMode}
          selectedEntitiesCount={selectedEntitiesCount}
        />
      )}
    </div>
  );
};
